import type { Id } from '@contracts/common';
import type { BatchRowSource, IngestJobDTO } from '@contracts/ingest';
import { notify } from '../../../lib/toast';
import { openDocument } from './api';

export const SOURCE_GONE = 'Không mở được tài liệu này';

/**
 * Where in its document a row was read: "tr.2" on the card, "trang 2" for a screen reader. Page → row → cell (D182),
 * as the page's "Dòng" column and `SourceLine`: a statement row's `cell` is the amount's column letter alone (L2
 * mapping), which tells no row from another.
 */
function placeOf(source: BatchRowSource): { short: string; long: string } | null {
  if (source.page != null) return { short: `tr.${source.page}`, long: `trang ${source.page}` };
  if (source.row != null) return { short: `dòng ${source.row}`, long: `dòng ${source.row}` };
  if (source.cell != null) return { short: `ô ${source.cell}`, long: `ô ${source.cell}` };
  return null;
}

/**
 * Opens an evidence original (D124): the window is opened synchronously on the click (a popup blocker lets a click's
 * own window through) and pointed at the 60 s URL after the await — a PDF at `page`, an image as it is. Anything else
 * downloads. A document the server does not give (404, D105) reads `SOURCE_GONE`, and the empty window closes.
 */
export function openOriginal(doc: { id: Id; filename: string; pdf: boolean; inline: boolean; page?: number | undefined }) {
  const win = doc.inline ? window.open('', '_blank') : null;
  if (win !== null) win.opener = null;
  openDocument(doc.id, doc.inline).then(({ url }) => {
    const target = doc.pdf && doc.page != null ? `${url}#page=${doc.page}` : url;
    if (win !== null) {
      win.location.href = target;
      return;
    }
    const a = window.document.createElement('a');
    a.href = target;
    a.rel = 'noopener';
    a.download = doc.filename;
    a.click();
  }, () => {
    win?.close();
    notify.error(SOURCE_GONE);
  });
}

/**
 * "↗" (S5 §8.1; L3 D124): the original the row was read from, opened by `openOriginal`. Other documents (DOCX, XLSX,
 * CSV, text) have no deep link and download. A pasted text has no original.
 */
export function SourceLink({ job, source, compact = false }: {
  job: IngestJobDTO; source: BatchRowSource;
  /** The page's table names the place in its own column (§8.2): the link is the arrow alone. */
  compact?: boolean;
}) {
  const document = job.sourceKind === 'text' ? undefined : job.documents[source.inputOrd];
  if (document === undefined) return null;
  const place = placeOf(source);
  const pdf = document.mediaType === 'application/pdf';
  const inline = pdf || document.mediaType.startsWith('image/');

  const open = () => openOriginal({ id: document.id, filename: document.filename, pdf, inline, page: source.page });

  return (
    <button type="button" onClick={open}
      aria-label={place === null ? 'Mở bản gốc' : `Mở bản gốc, ${place.long}`}
      className="-my-1 flex min-h-11 shrink-0 items-start whitespace-nowrap rounded-md px-1 pt-1 text-[11px] text-[hsl(var(--h-muted-foreground))] transition-colors hover:text-[hsl(var(--h-primary))] lg:min-h-0">
      {place === null || compact ? '↗' : `${place.short} ↗`}
    </button>
  );
}
